import React, { useState } from 'react';
import { Package, Boxes, X, Send, Check, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    key: 'half_kit',
    label: 'Half Kit',
    icon: Package,
    tagline: 'Mid-volume research supply',
    bullet: 'Best for small clinics & research collectives',
    discountCopy: 'Volume pricing · quoted on request',
  },
  {
    key: 'full_kit',
    label: 'Full Kit',
    icon: Boxes,
    tagline: 'Maximum-volume wholesale tier',
    bullet: 'Best for distributors & large research labs',
    discountCopy: 'Wholesale rate · quoted on request',
  },
];

const BulkOrderSection = ({ product }) => {
  const [activeTier, setActiveTier] = useState(null); // 'half_kit' | 'full_kit'
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [qty, setQty] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setActiveTier(null);
    setName('');
    setContact('');
    setQty('');
    setMessage('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/bulk-inquiries`, {
        product_id: product.id,
        product_name: product.name,
        tier: activeTier,
        name: name.trim(),
        contact: contact.trim(),
        preferred_quantity: qty.trim() || null,
        message: message.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError("Couldn't submit — try again in a moment or DM us on Discord.");
    } finally {
      setSubmitting(false);
    }
  };

  const tierMeta = TIERS.find((t) => t.key === activeTier);

  return (
    <section data-testid="bulk-order-section" className="mt-8">
      <div
        className="rounded-lg p-6 sm:p-8"
        style={{
          background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
          border: '1px solid rgba(212, 163, 115, 0.45)',
          boxShadow: '0 0 32px rgba(212, 163, 115, 0.08)',
        }}
      >
        <div className="mb-6">
          <span
            className="inline-block text-[10px] font-extrabold uppercase tracking-[0.25em] px-2.5 py-1 rounded-full mb-3"
            style={{
              background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
              color: '#0C0A09',
            }}
          >
            Wholesale Inquiries
          </span>
          <h3
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}
          >
            Wholesale & Volume Orders
          </h3>
          <p className="text-sm sm:text-base" style={{ color: '#B5A79E' }}>
            Select a tier and I'll follow up personally with a custom quote for{' '}
            <span className="font-bold" style={{ color: '#F7F3EE' }}>
              {product.name}
            </span>
            .
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            return (
              <button
                key={tier.key}
                type="button"
                onClick={() => setActiveTier(tier.key)}
                data-testid={`bulk-tier-${tier.key}`}
                className="text-left rounded-lg p-5 transition-all hover:scale-[1.02]"
                style={{
                  backgroundColor: '#0C0A09',
                  border: '1px solid rgba(212, 163, 115, 0.35)',
                  cursor: 'pointer',
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                    }}
                  >
                    <Icon className="h-6 w-6" style={{ color: '#0C0A09' }} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-lg" style={{ color: '#F7F3EE' }}>
                      {tier.label}
                    </p>
                    <p className="text-xs mb-2" style={{ color: '#B5A79E' }}>
                      {tier.tagline}
                    </p>
                    <p className="text-xs" style={{ color: '#D4A373' }}>
                      ✓ {tier.bullet}
                    </p>
                    <p
                      className="text-[10px] uppercase tracking-widest mt-2 font-bold"
                      style={{ color: '#86EFAC' }}
                    >
                      {tier.discountCopy}
                    </p>
                  </div>
                </div>
                <div
                  className="mt-4 text-center text-xs font-bold uppercase tracking-wider py-2 rounded-md"
                  style={{
                    backgroundColor: 'rgba(212, 163, 115, 0.12)',
                    border: '1px solid rgba(212, 163, 115, 0.35)',
                    color: '#D4A373',
                  }}
                >
                  Request Custom Quote →
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-xs mt-4 text-center" style={{ color: '#5A4A3F' }}>
          Pricing is provided on a per-inquiry basis to ensure the most competitive rate for your volume.
        </p>
      </div>

      {/* Modal */}
      {activeTier && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(12, 10, 9, 0.85)' }}
          onClick={reset}
        >
          <div
            data-testid="bulk-inquiry-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-lg overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
              border: '1px solid rgba(212, 163, 115, 0.5)',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-start justify-between p-5"
              style={{
                background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                color: '#0C0A09',
              }}
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold">
                  Bulk Inquiry · {tierMeta?.label}
                </p>
                <p className="text-lg font-bold mt-0.5">{product.name}</p>
              </div>
              <button
                type="button"
                onClick={reset}
                aria-label="Close"
                data-testid="close-bulk-modal"
                className="rounded-full p-1 hover:bg-black/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            {submitted ? (
              <div className="p-6 text-center">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.15)',
                    border: '2px solid rgba(34, 197, 94, 0.5)',
                  }}
                >
                  <Check className="h-8 w-8" style={{ color: '#86EFAC' }} />
                </div>
                <h4 className="text-xl font-bold mb-2" style={{ color: '#F7F3EE' }}>
                  Request received
                </h4>
                <p className="text-sm mb-5" style={{ color: '#B5A79E' }}>
                  We'll reach out to you at{' '}
                  <span className="font-bold" style={{ color: '#D4A373' }}>
                    {contact}
                  </span>{' '}
                  within 24 hours with a custom quote and your bulk discount.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="bulk-success-close"
                  className="px-6 py-2.5 rounded-md font-bold text-sm uppercase tracking-wide"
                  style={{
                    background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                    color: '#0C0A09',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
                <p className="text-sm" style={{ color: '#B5A79E' }}>
                  Share your contact details and I'll respond personally with a custom quote for{' '}
                  <strong style={{ color: '#F7F3EE' }}>{tierMeta?.label}</strong> volume.
                </p>

                <div>
                  <label
                    className="text-[10px] uppercase tracking-widest font-bold block mb-1.5"
                    style={{ color: '#B5A79E' }}
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    required
                    data-testid="bulk-name-input"
                    placeholder="e.g. Alex M."
                    className="w-full px-4 py-3 rounded-md focus:outline-none"
                    style={{
                      backgroundColor: '#0C0A09',
                      border: '1px solid #362B25',
                      color: '#F7F3EE',
                    }}
                  />
                </div>

                <div>
                  <label
                    className="text-[10px] uppercase tracking-widest font-bold block mb-1.5"
                    style={{ color: '#B5A79E' }}
                  >
                    Best Way To Reach You *
                  </label>
                  <input
                    type="text"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    maxLength={160}
                    required
                    data-testid="bulk-contact-input"
                    placeholder="Email, phone, Discord — whatever's easiest"
                    className="w-full px-4 py-3 rounded-md focus:outline-none"
                    style={{
                      backgroundColor: '#0C0A09',
                      border: '1px solid #362B25',
                      color: '#F7F3EE',
                    }}
                  />
                </div>

                <div>
                  <label
                    className="text-[10px] uppercase tracking-widest font-bold block mb-1.5"
                    style={{ color: '#B5A79E' }}
                  >
                    Quantity You Have In Mind <span style={{ color: '#5A4A3F' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    maxLength={80}
                    data-testid="bulk-qty-input"
                    placeholder="e.g. 15 vials, 50 vials, etc."
                    className="w-full px-4 py-3 rounded-md focus:outline-none"
                    style={{
                      backgroundColor: '#0C0A09',
                      border: '1px solid #362B25',
                      color: '#F7F3EE',
                    }}
                  />
                </div>

                <div>
                  <label
                    className="text-[10px] uppercase tracking-widest font-bold block mb-1.5"
                    style={{ color: '#B5A79E' }}
                  >
                    Anything Else? <span style={{ color: '#5A4A3F' }}>(optional)</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    data-testid="bulk-message-input"
                    placeholder="Timeline, related products you want bundled, payment preference…"
                    className="w-full px-4 py-3 rounded-md focus:outline-none resize-none"
                    style={{
                      backgroundColor: '#0C0A09',
                      border: '1px solid #362B25',
                      color: '#F7F3EE',
                    }}
                  />
                </div>

                {error && (
                  <p className="text-sm" style={{ color: '#FCA5A5' }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="bulk-submit-btn"
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md font-bold text-sm uppercase tracking-wide transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                    color: '#0C0A09',
                  }}
                >
                  {submitting ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Request Bulk Quote
                    </>
                  )}
                </button>

                <p className="text-xs text-center pt-1" style={{ color: '#5A4A3F' }}>
                  <MessageSquare className="h-3 w-3 inline mr-1" />
                  We'll get back to you within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default BulkOrderSection;
